import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import IconButton from './IconButton';
import ExerciseHistoryModal from './ExerciseHistoryModal';
import { createExercise } from '../services/exercisesService';
import { MUSCLE_GROUPS, MUSCLE_TAXONOMY } from '../constants/exerciseTaxonomy';
import './comic.css';

const entryButtonStyle = {
  flex: 1,
  minWidth: 0,
  color: "#232526",
  fontFamily: "'Anonymous Pro', monospace",
  textAlign: "left",
  cursor: "pointer",
  fontSize: "16px"
};

/**
 * Shared popup for picking an exercise: search by name, or browse a
 * collapsible list of muscle groups (each expands to reveal its exercises,
 * sub-grouped by main muscle) -- and, if it's not there yet, create it on
 * the spot without leaving the popup. Replaces the plain inline
 * ExercisePicker combobox in Quick Start, and doubles as "+Add Exercise"'s
 * popup in Manage My Exercises (per direct user request that both use "the
 * same popup"). In the latter context there's nothing coherent to DO with
 * picking an already-existing exercise (it's already in the list below), so
 * callers there just pass a no-op onSelect -- browsing/searching first still
 * has real value as a duplicate check before creating a new one.
 *
 * `onSelect(exercise)` is called both when an existing exercise is tapped
 * AND when a new one is successfully created (with the newly-created
 * exercise's shape), immediately followed by `onClose()` -- picking or
 * creating always closes the popup, there's no multi-select.
 *
 * `startCreating`, optional: opens straight into the create-new-exercise
 * form instead of the search/browse view -- Manage My Exercises' "+ Add
 * Exercise" trigger passes this, per direct user request to skip the
 * search/browse step there specifically (the whole point of that button is
 * creating a new one; the popup's "Back" link still reaches search/browse
 * from inside the form, in case the user changes their mind).
 */
const ExerciseSelectorModal = ({ exercises, userId, tabKey, title = "Select Exercise", startCreating = false, onSelect, onClose }) => {
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [creating, setCreating] = useState(startCreating);
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("");
  const [newMain, setNewMain] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Which exercise's "i" history popup is open: { exerciseId, name } or null.
  const [historyExercise, setHistoryExercise] = useState(null);

  const toggleGroup = (muscle) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(muscle)) next.delete(muscle);
      else next.add(muscle);
      return next;
    });
  };

  // Two-tier grouping (target muscle -> main muscle), same pattern as
  // RoutinePicker's "Tap to add exercises" builder -- "first present a list
  // of general body parts, each expands when tapped to reveal the exercises
  // it includes."
  const groups = useMemo(() => {
    const map = new Map();
    exercises.forEach((ex) => {
      const muscle = ex.targetMuscle || "Other";
      if (!map.has(muscle)) map.set(muscle, new Map());
      const subMap = map.get(muscle);
      const main = ex.mainMuscle || "Other";
      if (!subMap.has(main)) subMap.set(main, []);
      subMap.get(main).push(ex);
    });
    return Array.from(map.entries()).map(([muscle, subMap]) => ({
      muscle,
      subGroups: Array.from(subMap.entries()).map(([main, list]) => ({ main, list }))
    }));
  }, [exercises]);

  const trimmedSearch = search.trim().toLowerCase();
  const searchResults = trimmedSearch
    ? exercises.filter((ex) => ex.name.toLowerCase().includes(trimmedSearch))
    : null;

  const handlePick = (ex) => {
    onSelect(ex);
    onClose();
  };

  // Shared row for both the search-results list and the muscle-group/
  // main-muscle browse list below: the tap target picks the exercise, and a
  // separate "i" icon beside it opens that exercise's history popup without
  // closing this one.
  const renderExerciseRow = (ex, labelSuffix = "") => (
    <div key={ex.id} style={{ display: "flex", gap: "6px", alignItems: "stretch" }}>
      <button type="button" onClick={() => handlePick(ex)} className="comic-panel comic-panel--entry comic-tap" style={entryButtonStyle}>
        {ex.name}{labelSuffix}
      </button>
      <IconButton
        icon="info"
        onClick={() => setHistoryExercise({ exerciseId: ex.id, name: ex.name })}
        color="#232526"
        label={`View history for ${ex.name}`}
      />
    </div>
  );

  const openCreateForm = () => {
    setCreating(true);
    setNewName(search.trim());
    setNewGroup("");
    setNewMain("");
    setError(null);
  };

  const cancelCreating = () => {
    setCreating(false);
    setNewName("");
    setNewGroup("");
    setNewMain("");
    setError(null);
  };

  // Same case-insensitive/trimmed full-name duplicate guard used everywhere
  // else exercises are created in this app.
  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || !newGroup || saving) return;

    const isDuplicate = exercises.some((ex) => ex.name?.trim().toLowerCase() === name.toLowerCase());
    if (isDuplicate) {
      setError(`An exercise named "${name}" already exists.`);
      return;
    }

    setSaving(true);
    setError(null);
    const result = await createExercise(name, newGroup, newMain, userId);
    setSaving(false);
    if (result.success) {
      onSelect({ id: result.id, name, targetMuscle: newGroup, mainMuscle: newMain, ownerUserId: userId });
      onClose();
    } else {
      setError("Couldn't add that exercise. Try again.");
    }
  };

  return (
    <>
    <Modal title={creating ? "New Exercise" : title} tabKey={tabKey} onClose={onClose}>
      {creating ? (
        <form onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="Exercise name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="comic-input"
            style={{ width: "100%", marginBottom: "10px" }}
            autoFocus
          />
          <select
            value={newGroup}
            onChange={(e) => { setNewGroup(e.target.value); setNewMain(""); }}
            className="comic-input"
            style={{ width: "100%", marginBottom: "10px" }}
          >
            <option value="">Select Muscle Group</option>
            {MUSCLE_GROUPS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <select
            value={newMain}
            onChange={(e) => setNewMain(e.target.value)}
            disabled={!newGroup}
            className="comic-input"
            style={{ width: "100%" }}
          >
            <option value="">Select Main Muscle (Optional)</option>
            {(MUSCLE_TAXONOMY[newGroup] || []).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={saving || !newName.trim() || !newGroup}
            className="comic-btn comic-btn--go"
          >
            {saving ? "Saving..." : "Add to Library"}
          </button>
          <button type="button" onClick={cancelCreating} className="comic-btn comic-btn--end">
            Back
          </button>
          {error && <div className="comic-alert-bubble">{error}</div>}
        </form>
      ) : (
        <>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Select or Type to Search"
            className="comic-input"
            style={{ width: "100%", marginBottom: "10px" }}
            autoFocus
          />

          {trimmedSearch ? (
            searchResults.length === 0 ? (
              <p className="comic-empty">No exercises match "{search.trim()}".</p>
            ) : (
              searchResults.map((ex) => renderExerciseRow(ex, ex.mainMuscle ? ` (${ex.mainMuscle})` : ""))
            )
          ) : groups.length === 0 ? (
            <p className="comic-empty">No exercises in the library yet.</p>
          ) : (
            groups.map(({ muscle, subGroups }) => {
              const isOpen = openGroups.has(muscle);
              return (
                <div key={muscle}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(muscle)}
                    aria-expanded={isOpen}
                    className="comic-eyebrow"
                    style={{ marginTop: "10px", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    {muscle}
                    <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && subGroups.map(({ main, list }) => (
                    <div key={main}>
                      <div style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "12px", fontWeight: "bold", color: "#232526", opacity: 0.65, margin: "8px 0 4px 4px", textTransform: "uppercase" }}>
                        {main}
                      </div>
                      {list.map((ex) => renderExerciseRow(ex))}
                    </div>
                  ))}
                </div>
              );
            })
          )}

          <button type="button" onClick={openCreateForm} className="comic-btn comic-btn--go" style={{ marginTop: "14px" }}>
            + Create New Exercise
          </button>
        </>
      )}
    </Modal>

    {historyExercise && (
      <ExerciseHistoryModal
        exerciseId={historyExercise.exerciseId}
        exerciseName={historyExercise.name}
        userId={userId}
        tabKey={tabKey}
        onClose={() => setHistoryExercise(null)}
      />
    )}
    </>
  );
};

export default ExerciseSelectorModal;
